import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";

import { ArticleCover } from "./ArticleCover";
import { getCategory } from "../data/articleCategories";
import { LocalizedLink } from "./LocalizedLink";
import { useLanguage } from "../i18n/LanguageProvider";
import { formatReadTime } from "../i18n/paths";

export const ArticleCard = ({ article, index = 0 }) => {
  const { t, formatDate, pick } = useLanguage();
  const category = getCategory(article);

  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: Math.min(index, 6) * 0.05 }}
      data-testid={`article-card-${article.id}`}
      className="group h-full"
      style={{ "--cat": category.color, "--cat-rgb": category.rgb }}
    >
      <LocalizedLink
        to={`/articles/${article.slug}`}
        className="flex h-full flex-col overflow-hidden rounded-2xl border border-white/5 bg-[#0F0F10] transition-colors duration-300 hover:border-[rgba(var(--cat-rgb),0.45)]"
      >
        <ArticleCover article={article} className="aspect-[16/10]" iconSize={120} />

        <div className="flex flex-1 flex-col p-6">
          <div className="mb-3 flex items-center gap-3">
            <time className="text-xs font-medium text-zinc-600">
              {formatDate(article.date, { year: "numeric", month: "short", day: "numeric" })}
            </time>
            <span className="h-1 w-1 rounded-full bg-zinc-700" />
            <span className="flex items-center gap-1 text-xs text-zinc-600">
              <Clock size={12} strokeWidth={1.5} />
              {formatReadTime(article.readTime, t)}
            </span>
          </div>

          <div className="mb-2 flex items-start justify-between gap-3">
            <h3 className="line-clamp-2 text-lg font-medium leading-snug text-white transition-colors duration-200 group-hover:text-[var(--cat)]">
              {pick(article, "title")}
            </h3>
            <ArrowUpRight
              size={18}
              strokeWidth={1.5}
              className="mt-0.5 shrink-0 text-zinc-700 transition-colors duration-200 group-hover:text-[var(--cat)]"
            />
          </div>

          <p className="line-clamp-2 text-sm leading-relaxed text-zinc-500">{pick(article, "description")}</p>

          <div className="mt-4 flex flex-wrap gap-2 pt-4 border-t border-white/5">
            {article.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center rounded-full bg-white/5 px-2.5 py-0.5 text-xs font-medium text-zinc-400"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </LocalizedLink>
    </motion.article>
  );
};
